import { Injectable, signal, OnDestroy } from '@angular/core';

interface VersionInfo {
    version: string;
    buildTime: string;
}

/**
 * Polls /version.json to detect new deployments.
 * When a new version is found, sets `updateAvailable` to true so the UI can show
 * an update banner or snackbar.
 */
@Injectable({ providedIn: 'root' })
export class VersionService implements OnDestroy {

    /** The current known build version hash */
    readonly currentVersion = signal<string>('');

    /** Whether a new version has been detected */
    readonly updateAvailable = signal<boolean>(false);

    private pollIntervalId: ReturnType<typeof setInterval> | null = null;
    private readonly POLL_INTERVAL_MS = 5 * 60_000; // 5 minutes

    /**
     * Start polling for version changes.
     * Call this from AppComponent.ngOnInit() so it only runs once.
     */
    startPolling(): void {
        // Fetch initial version
        this.checkVersion().then();

        // Poll every 5 minutes
        this.pollIntervalId = setInterval(() => {
            this.checkVersion().then();
        }, this.POLL_INTERVAL_MS);
    }

    /**
     * Manually trigger a version check (e.g. from clicking the version number).
     * Returns true if a new version was found.
     */
    async checkNow(): Promise<boolean> {
        await this.checkVersion();
        return this.updateAvailable();
    }

    private async checkVersion(): Promise<void> {
        try {
            // Cache-bust to always get latest version.json
            const response = await fetch(`/version.json?t=${Date.now()}`);
            if (!response.ok) return;

            const data = await response.json() as VersionInfo;
            const newVersion = data.version;

            if (!this.currentVersion()) {
                // First load – store current version
                this.currentVersion.set(newVersion);
            } else if (newVersion !== this.currentVersion()) {
                // Version changed – a new deploy happened
                this.updateAvailable.set(true);
            }
        } catch {
            // Silently ignore fetch errors (offline, 404 during dev, etc.)
        }
    }

    /** Force reload to get new version */
    applyUpdate(): void {
        window.location.reload();
    }

    ngOnDestroy(): void {
        if (this.pollIntervalId) {
            clearInterval(this.pollIntervalId);
        }
    }
}
